import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  CalendarBlank, Lightning, X, ListNumbers,
} from '@phosphor-icons/react';
import HomeAccordion from '../components/HomeAccordion';
import HomeFeaturedHero from '../components/home/HomeFeaturedHero';
import ProfileGenderNudge from '../components/home/ProfileGenderNudge';
import { LadderRow, SessionRow, ChallengeRow, ClubEventRow } from '../components/events/MyEventsRows';
import useMyEvents from '../hooks/useMyEvents';
import useHomeData from '../hooks/useHomeData';
import { EVENTS } from '../lib/eventsCatalog';
import { ladderAPI } from '../lib/api';

const WELCOME_DISMISSED_KEY = 'tl_home_welcome_dismissed_v1';
const HOME_VISIBLE_LIMIT = 3;

export default function HomePage() {
  const { user, player, hasLadderAccess, managedSessions, managedEvents, accountStatus, refreshUser } = useAuth();
  const navigate = useNavigate();

  // One-time welcome banner persistence (unchanged).
  const [bannerDismissed, setBannerDismissed] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem(WELCOME_DISMISSED_KEY) === '1',
  );
  const dismissBanner = () => {
    try { localStorage.setItem(WELCOME_DISMISSED_KEY, '1'); } catch { /* ignore */ }
    setBannerDismissed(true);
  };

  // My Events (ladder + sessions, with court_number for in-progress)
  const { rows: myEventsRows, loading: myEventsLoading, ladder, sessions: mySessions } = useMyEvents({
    user,
    player,
    hasLadderAccess,
    managedSessions,
  });

  // Active Challenge data
  const playerId = player?.id || null;
  const { actionableChallenge, counts, loading: challengesLoading } = useHomeData(playerId);

  // Live ladder statuses — archived ladders drop out of the Browse Events
  // catalog so members no longer see a ladder an admin has archived.
  const [visibleLadderSlugs, setVisibleLadderSlugs] = useState(null);
  useEffect(() => {
    let cancelled = false;
    ladderAPI.listLadders()
      .then((a) => {
        if (cancelled) return;
        setVisibleLadderSlugs(new Set((a.data?.ladders || []).map((l) => l.slug)));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const firstName =
    user?.first_name || (user?.email ? user.email.split('@')[0] : 'there');

  // ----- Active Challenge subtitle + compact summary line -----
  //
  // Compose ONE clean line for the closed subtitle:
  //   "Rank #5 · No active challenge"          (ladder, no challenge)
  //   "Rank #5 · Pending challenge from Sophia" (ladder + incoming pending)
  //   "Rank #5 · Challenge vs Aaron accepted"   (ladder + accepted)
  //   "Pending challenge from Sophia"           (no ladder rank, has challenge)
  //
  // The accordion's expanded body then shows the same key info + the CTA.
  let challengeStatusText = null;
  let challengeCTA = null;
  let challengeRoute = '/challenges';
  if (actionableChallenge) {
    const ac = actionableChallenge;
    const oppName =
      (ac.challenger_id === playerId ? ac.challenged_name : ac.challenger_name) || 'opponent';
    if (ac.status === 'pending' && ac.challenged_id === playerId) {
      challengeStatusText = `Pending challenge from ${oppName}`;
      challengeCTA = 'Respond';
    } else if (ac.status === 'accepted') {
      challengeStatusText = `Challenge vs ${oppName} accepted`;
      challengeCTA = 'View challenge';
    } else if (ac.status === 'pending') {
      challengeStatusText = `Awaiting ${oppName}'s response`;
      challengeCTA = 'View challenge';
    }
  } else if (hasLadderAccess) {
    challengeStatusText = 'No active challenge';
    challengeCTA = 'Open ladder';
    challengeRoute = '/ladder';
  }

  const rankText = ladder?.rank ? `Rank #${ladder.rank}` : null;
  // Compact one-liner for the closed accordion header
  const challengeSubtitle = [rankText, challengeStatusText]
    .filter(Boolean)
    .join(' · ') || 'No active challenges right now.';

  // Hide accordion entirely when user has no ladder access AND no challenge
  const showActiveChallenge = hasLadderAccess || actionableChallenge;

  // My Events subtitle
  const myEventsSubtitle = myEventsLoading
    ? 'Loading…'
    : myEventsRows.length === 0
      ? "You haven't joined any club events yet."
      : myEventsRows.length === 1
        ? 'Your active club event'
        : `${myEventsRows.length} active club events`;

  // Featured catalog preview (max 3)
  const catalogActive = EVENTS.filter((e) => e.status === 'active');
  const catalogComingSoon = EVENTS.filter((e) => e.status === 'coming_soon');
  // Order active first, then coming-soon to fill out the top 3.
  const catalogPreview = [...catalogActive, ...catalogComingSoon].slice(0, HOME_VISIBLE_LIMIT);
  // Drop archived ladders (absent from the live list) once statuses load.
  const catalogPreviewVisible = visibleLadderSlugs
    ? catalogPreview.filter((e) => !e.always_open || visibleLadderSlugs.has(e.slug))
    : catalogPreview;

  return (
    <div className="space-y-4" data-testid="home-page">
      {/* Welcome header — the app shell already shows "The Rippers Club"
          in the top bar; this page just greets the member. */}
      <div>
        <h1
          className="font-serif text-4xl sm:text-5xl text-[#013523] tracking-tight"
          data-testid="home-welcome-heading"
        >
          Welcome, {firstName}.
        </h1>
        <p className="text-sm text-[#6a766e] mt-1">
          Here's what's happening at the club.
        </p>
      </div>

      {/* Gender profile-completion nudge — appears only when the approved
          member's gender is unset. Dismissable per-session. Never nags. */}
      <ProfileGenderNudge user={user} accountStatus={accountStatus} onSaved={refreshUser} />

      {/* One-time welcome banner */}
      {!bannerDismissed && (
        <div
          data-testid="home-welcome-banner"
          className="bg-[#015e3f]/10 border border-[#015e3f]/30 rounded-2xl p-4 flex items-start gap-3"
          role="status"
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[#013523] font-medium">
              Welcome to the new club home.
            </p>
            <p className="text-xs text-[#2a332c] mt-1">
              Tap each section below to see what's happening. We'll add more club activities here as they launch.
            </p>
          </div>
          <button
            type="button"
            data-testid="home-welcome-banner-dismiss"
            onClick={dismissBanner}
            className="shrink-0 text-[#6a766e] hover:text-[#013523] p-1 rounded-lg"
            aria-label="Dismiss welcome message"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
      )}

      {/* Prompt 4 — Admin-curated Home Featured rotation. Renders nothing
          (returns null) when the admin hasn't featured anything. Never
          falls back to random events. */}
      <HomeFeaturedHero />

      {/* 1. My Events — open by default so users immediately see their
          registered events (or the motivational empty state). */}
      <HomeAccordion
        testId="home-accordion-my-events"
        title="My Events"
        subtitle={myEventsSubtitle}
        icon={CalendarBlank}
        variant="primary"
        defaultOpen
      >
        {myEventsLoading && (
          <div className="text-xs text-[#6a766e]">Loading…</div>
        )}
        {!myEventsLoading && myEventsRows.length === 0 && (
          <div className="space-y-3" data-testid="home-my-events-empty">
            <div>
              <p className="text-sm text-[#013523] font-medium">
                You're not registered for anything yet.
              </p>
              <p className="text-xs text-[#6a766e] mt-1">
                Jump into a ladder, night, tournament, or club event and get in on the fun.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/events')}
              data-testid="home-my-events-browse-link"
              className="inline-flex items-center justify-center px-3 py-2 rounded-full bg-[#015e3f] text-white text-xs font-medium hover:bg-[#013523] transition-colors"
            >
              Browse all events →
            </button>
          </div>
        )}
        {!myEventsLoading && myEventsRows.length > 0 && (
          <>
            <div className="space-y-2" data-testid="home-my-events-list">
              {myEventsRows.slice(0, HOME_VISIBLE_LIMIT).map((row) => {
                if (row.kind === 'ladder') {
                  return (
                    <LadderRow
                      key="ladder"
                      data={row.data}
                      onClick={() => navigate('/ladder')}
                    />
                  );
                }
                // Prompt 2.2 fix: route Tournaments / Round Robins to their
                // own detail pages. The previous implementation sent every
                // non-ladder row through /events/sessions/:id, producing a
                // "Session not found" error for tournament and RR cards.
                if (row.kind === 'tournament') {
                  const canMgTournament = user?.role === 'admin'
                    || (Array.isArray(managedEvents) && managedEvents.some((m) => m.event_kind === 'tournament' && m.event_id === row.data.id));
                  return (
                    <SessionRow
                      key={`t-${row.data.id}`}
                      session={{
                        id: row.data.id,
                        name: row.data.name,
                        status: row.data.status,
                        gender: 'mens',   // unused for routing; SessionRow's sub-line falls back cleanly
                        starts_at: row.data.starts_at,
                        my_registration: null,
                        is_alternate: !!row.data.is_alternate,
                      }}
                      onClick={() => navigate(`/tournaments/${row.data.id}`)}
                      canManage={canMgTournament}
                      onManage={(s) => navigate(`/tournaments/${s.id}/manage`)}
                    />
                  );
                }
                if (row.kind === 'round_robin') {
                  const canMgRR = user?.role === 'admin'
                    || (Array.isArray(managedEvents) && managedEvents.some((m) => m.event_kind === 'round_robin' && m.event_id === row.data.id));
                  return (
                    <SessionRow
                      key={`rr-${row.data.id}`}
                      session={{
                        id: row.data.id,
                        name: row.data.name,
                        status: row.data.status,
                        gender: 'mens',
                        starts_at: row.data.starts_at,
                        my_registration: null,
                        is_alternate: !!row.data.is_alternate,
                      }}
                      onClick={() => navigate(`/round-robins/${row.data.id}`)}
                      canManage={canMgRR}
                      onManage={(s) => navigate(`/round-robins/${s.id}/manage`)}
                    />
                  );
                }
                if (row.kind === 'club_event') {
                  return (
                    <ClubEventRow
                      key={`ce-${row.data.id}`}
                      event={row.data}
                      onClick={() => navigate(`/club-events/${row.data.id}`)}
                    />
                  );
                }
                return (
                  <SessionRow
                    key={row.data.id}
                    session={row.data}
                    onClick={() => navigate(`/events/sessions/${row.data.id}`)}
                    canManage={managedSessions?.includes(row.data.id)}
                    onManage={(s) => navigate(`/admin/events/${s.id}`)}
                  />
                );
              })}
            </div>
            {myEventsRows.length > HOME_VISIBLE_LIMIT && (
              <button
                type="button"
                onClick={() => navigate('/events?tab=mine')}
                data-testid="home-my-events-view-all"
                className="text-xs font-medium text-[#015e3f] mt-3 hover:underline"
              >
                View all events ({myEventsRows.length}) →
              </button>
            )}
          </>
        )}
      </HomeAccordion>

      {/* 2. Active Challenges — compact row matching the My Events style.
          Hidden entirely when the user has no ladder access AND no
          actionable challenge (the section would otherwise be irrelevant). */}
      {showActiveChallenge && (
        <HomeAccordion
          testId="home-accordion-challenges"
          title="Active Challenges"
          subtitle={challengeSubtitle}
          icon={Lightning}
          variant="challenge"
        >
          <div className="space-y-3" data-testid="home-challenges-body">
            <ChallengeRow
              rank={ladder?.rank}
              statusText={challengeStatusText || 'No active challenge'}
              subText={
                (counts.incoming > 0 || counts.outgoing > 0 || counts.accepted > 0)
                  ? [
                      counts.incoming > 0 ? `${counts.incoming} incoming` : null,
                      counts.accepted > 0 ? `${counts.accepted} in progress` : null,
                      counts.outgoing > 0 ? `${counts.outgoing} awaiting response` : null,
                    ].filter(Boolean).join(' · ')
                  : null
              }
              ctaLabel={challengeCTA}
              onClick={() => navigate(challengeRoute)}
            />
            <button
              type="button"
              onClick={() => navigate('/challenges')}
              data-testid="home-challenges-view-all"
              className="text-xs font-medium text-[#8a6418] hover:underline"
            >
              View all challenges →
            </button>
          </div>
        </HomeAccordion>
      )}

      {/* 3. Browse Events — catalog preview */}
      <HomeAccordion
        testId="home-accordion-all-events"
        title="Browse Events"
        subtitle="Browse club event types"
        icon={ListNumbers}
        variant="events"
      >
        <ul className="space-y-2" data-testid="home-all-events-list">
          {catalogPreviewVisible.map((ev) => {
            // Per-event CTA copy. The Singles Ladder is the only catalog
            // item with viewer-state-dependent wording today: non-members
            // see "View & Join"; members see "Open Ladder". Other active
            // events share a neutral "Open" pill; coming-soon stay grey.
            let ctaLabel = ev.status === 'active' ? 'Open' : 'Coming soon';
            if (ev.slug === 'mens-singles-ladder') {
              ctaLabel = hasLadderAccess ? 'Open Ladder' : 'View & Join';
            }
            const isClickable = ev.status === 'active';
            return (
              <li key={ev.slug}>
                <button
                  type="button"
                  onClick={() => {
                    if (isClickable && ev.route) navigate(ev.route);
                    else navigate('/events?tab=catalog');
                  }}
                  data-testid={`home-all-events-${ev.slug}`}
                  className="w-full text-left bg-[#f7faf8] border border-[#d4ddd7] rounded-xl p-3 flex items-center justify-between gap-3 hover:bg-[#eef3ef]/70 transition-colors"
                >
                  <div className="min-w-0">
                    <div className="text-sm text-[#013523] font-medium truncate">{ev.name}</div>
                    <div className="text-[11px] text-[#3f6a56] mt-0.5">
                      {ev.status === 'active' ? 'Open' : 'Coming soon'}
                    </div>
                  </div>
                  <span
                    data-testid={`home-all-events-${ev.slug}-cta`}
                    className={`shrink-0 text-[11px] font-medium rounded-full px-3 py-1 whitespace-nowrap ${
                      isClickable
                        ? 'bg-[#015e3f] text-white'
                        : 'bg-[#e3dcd1]/60 text-[#6a766e]'
                    }`}
                  >
                    {ctaLabel}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        {EVENTS.length > HOME_VISIBLE_LIMIT && (
          <button
            type="button"
            onClick={() => navigate('/events')}
            data-testid="home-all-events-view-all"
            className="text-xs font-medium text-[#3f6a56] mt-3 hover:underline"
          >
            View all events →
          </button>
        )}
      </HomeAccordion>

      {/* 4. Club News — hidden in Slice 2 of the Home redesign. Preserved
          here for re-enablement when announcements ship.
          {' '}
          <HomeAccordion testId="home-accordion-club-news" title="Club News"
            subtitle="Coming soon" icon={Newspaper} variant="news"> ... </HomeAccordion>
      */}

      {/* 5. Calendar — hidden in Slice 2 of the Home redesign. Preserved
          here for re-enablement when the club calendar ships.
          {' '}
          <HomeAccordion testId="home-accordion-calendar" title="Calendar"
            subtitle="Coming soon" icon={Calendar} variant="calendar"> ... </HomeAccordion>
      */}
    </div>
  );
}
